import { Component, ErrorInfo, ReactNode } from "react";
import { logAppRootError } from "@/lib/errorLoggingACTIONS";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info.componentStack);
    logAppRootError(error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        data-testid="ErrorBoundaryContainer"
        id="ErrorBoundaryContainer"
        className="flex min-h-screen items-center justify-center bg-[#111111]"
      >
        <div className="flex max-w-sm flex-col items-center px-6 text-center">
          {/* Warning icon */}
          <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-[#2d1e0e]">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7 text-orange-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>

          <h1 className="mb-3 text-2xl font-bold text-white">
            Something went wrong
          </h1>

          <p className="mb-8 text-sm leading-relaxed text-gray-400">
            A component failed to render. This is usually temporary, try
            refreshing the page. If the problem persists, contact support.
          </p>

          <div className="mb-8 flex gap-3">
            <button
              onClick={this.handleReload}
              className="flex cursor-pointer items-center gap-2 rounded-md bg-orange-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-600"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
              Refresh page
            </button>

            <a
              href="mailto:helpdesk@liquiditycube.com"
              className="flex items-center gap-2 rounded-md border border-gray-600 bg-[#1e1e1e] px-4 py-2 text-sm font-medium text-white no-underline transition-colors hover:bg-[#2a2a2a]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              Email helpdesk
            </a>
          </div>

          <p className="text-xs text-gray-600">
            Error code: RENDER_FAIL · Liquidity Cube
          </p>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
